import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpotify, faApple, faGoogle } from '@fortawesome/free-brands-svg-icons';

export const metadata = {
  title: 'GM Home Remodeling: Insider Stories from Contractors',
  date: 'February 6, 2026',
  author: 'Modern Build Team',
  excerpt:
    'We discuss real experiences from general contractors, covering tips, lessons learned, and behind-the-scenes industry insights.',
  category: 'Home Remodeling',
  tags: ['contractors', 'home remodeling', 'industry tips', 'behind the scenes'],
  youtubeId: 'hyG-KF1zWJI',
  guest: 'Gal', // contractor first name
  companyName: 'GM Home Remodeling',
  guestWebsite: 'http://gmhomeremodeling.com',
};

export default function GMHomeRemodeling() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link rel="canonical" href={`https://modernbuildremodel.com/podcast/gm-home-remodeling`} />

        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://img.youtube.com/vi/${metadata.youtubeId}/maxresdefault.jpg`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/podcast/gm-home-remodeling`} />
        <meta property="og:type" content="video.other" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://img.youtube.com/vi/${metadata.youtubeId}/maxresdefault.jpg`} />
        <meta name="twitter:site" content="@modernbuildhome" />
      </Head>

      <Header />

      <div className="bg-white">
        {/* Episode Hero */}
        <Hero category="episodeHeroDefault" page="page" buttonLink="https://www.youtube.com/@modernbuildhomeremodeling" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          {/* YouTube Video */}
          <div className="aspect-video mb-8">
            <iframe
              src={`https://www.youtube.com/embed/${metadata.youtubeId}`}
              title={metadata.title}
              allowFullScreen
              className="w-full h-full rounded-lg"
            />
          </div>

          <p className="text-sm text-black mb-4">
            <span className="capitalize text-primary tracking-[3px]">
              By {metadata.author} / {metadata.date}
            </span>
          </p>

          <div className="prose prose-lg max-w-none text-black leading-relaxed space-y-6">
            <p>
              In this episode of <strong>Behind the Build</strong>, we sit
              down with <strong>{metadata.guest}</strong> from{' '}
              <strong>
                <a
                  href={metadata.guestWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {metadata.companyName}
                </a>
              </strong>{' '}
              to explore the life of a general contractor and home service pro. You’ll hear stories,
              lessons, and insider knowledge from years on the job.
            </p>

            <p>
              We cover topics like nightmare or dream clients, budgeting and average project costs,
              tips for homeowners, choosing (and keeping) the right contractor, behind-the-scenes
              industry insights, dirty jobs and side stories, career paths, tools, tech, trends,
              insurance, remodels, and pretty much the whole home improvement world.
            </p>

            <h3>Related Services</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <a
                  href="/kitchen-remodeling/california/los-angeles/90001"
                  className="text-primary underline"
                >
                  Kitchen Remodeling in Los Angeles
                </a>
              </li>
              <li>
                <a
                  href="/bathroom-remodeling/california/los-angeles/90001"
                  className="text-primary underline"
                >
                  Bathroom Remodeling in Los Angeles
                </a>
              </li>
            </ul>

            {/* Podcast Platform Logos */}
            <h3 className="text-xl font-semibold text-center mt-10">Listen on Your Favorite Platform</h3>
            <div className="flex justify-evenly mt-4 items-center w-full max-w-md mx-auto">

              {/* Spotify */}
              <a
                href="https://open.spotify.com/show/YOUR_SPOTIFY_SHOW_ID"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 text-primary hover:text-green-500 transition"
              >
                <FontAwesomeIcon icon={faSpotify} className="text-[30px] md:text-[36px]" />
                <span className="text-sm font-light text-black">Spotify</span>
              </a>

              {/* Apple Podcasts */}
              <a
                href="https://podcasts.apple.com/us/podcast/YOUR_APPLE_PODCAST_LINK"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 text-primary hover:text-black transition"
              >
                <FontAwesomeIcon icon={faApple} className="text-[30px] md:text-[36px]" />
                <span className="text-sm font-light text-black">Apple Podcasts</span>
              </a>

              {/* Google Podcasts */}
              <a
                href="https://www.google.com/podcasts?feed=YOUR_GOOGLE_PODCAST_FEED"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 text-primary hover:text-blue-500 transition"
              >
                <FontAwesomeIcon icon={faGoogle} className="text-[30px] md:text-[36px]" />
                <span className="text-sm font-light text-black">Google Podcasts</span>
              </a>

            </div>
          </div>
        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
      </div>
    </>
  );
}
